import { useState } from "react";
import { toast } from "sonner";
import { CONTACT_EMAILS, CONTACT_REASONS } from "@/data/content";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", reason: "General enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ "form-name": "atlantic-contact", ...form }).toString(),
      });
      if (!res.ok) throw new Error("Contact submission failed");
      toast.success("Thank you. Your message has been received.");
      setForm({ name: "", email: "", reason: "General enquiry", message: "" });
    } catch (err) {
      toast.error("Please check the form fields and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-atlantic/20 bg-white px-4 py-3 text-sm text-atlantic placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

  return (
    <section id="contact" data-testid="contact-section" className="bg-sand py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 lg:grid-cols-12 lg:gap-12 lg:px-8">
        <Reveal className="lg:col-span-5">
          <SectionHeading
            kicker="Contact"
            title="Start a Conversation"
            copy="Story leads, corrections, research enquiries and partnership proposals are welcome."
          />
          <div className="mt-10 space-y-4">
            {CONTACT_EMAILS.map((c) => (
              <div key={c.label} className="border-b border-atlantic/10 pb-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{c.label}</p>
                <p data-testid={`contact-email-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mt-1.5 text-sm italic leading-relaxed text-slate-500">
                  {c.email ? <a href={`mailto:${c.email}`} className="font-medium text-ocean underline underline-offset-2">{c.note}</a> : c.note}
                </p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-6 lg:col-start-7">
          <form name="atlantic-contact" data-netlify="true" data-testid="contact-form" onSubmit={submit} className="border border-atlantic/10 bg-white p-7 sm:p-9">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Name</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Email</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.org" className={`mt-2 ${inputCls}`} />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-reason" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Reason for contact</label>
              <select id="contact-reason" data-testid="contact-reason-select" value={form.reason} onChange={set("reason")} className={`mt-2 ${inputCls}`}>
                {CONTACT_REASONS.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Message</label>
              <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`mt-2 resize-y ${inputCls}`} />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="mt-7 w-full bg-atlantic px-6 py-3.5 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-atlantic-light disabled:opacity-60"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
